/**
 * Callout rendering for `> [!type] Title` blockquotes (the Obsidian
 * syntax the slash menu already inserts). The type marker renders as a
 * colored icon + bolded title; the whole quote gets a tinted bar.
 */
import { Decoration, type DecorationSet, EditorView, ViewPlugin, type ViewUpdate, WidgetType } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";
import type { Range } from "@codemirror/state";

const CALLOUTS: Record<string, { icon: string; hue: string }> = {
  note: { icon: "✎", hue: "var(--info)" },
  info: { icon: "ℹ", hue: "var(--info)" },
  tip: { icon: "☀", hue: "var(--accent-text)" },
  success: { icon: "✓", hue: "var(--success)" },
  question: { icon: "?", hue: "var(--accent-text)" },
  warning: { icon: "⚠", hue: "var(--warning)" },
  danger: { icon: "✕", hue: "var(--danger)" },
  caution: { icon: "✕", hue: "var(--danger)" },
  quote: { icon: "❝", hue: "var(--text-muted)" },
};

const MARKER_RE = /^\[!(\w+)\]\s?/;

class CalloutIconWidget extends WidgetType {
  constructor(readonly type: string) {
    super();
  }
  override eq(other: CalloutIconWidget) {
    return other.type === this.type;
  }
  toDOM() {
    const info = CALLOUTS[this.type] ?? CALLOUTS["note"]!;
    const span = document.createElement("span");
    span.className = "hn-callout-icon";
    span.style.color = info.hue;
    span.textContent = info.icon;
    return span;
  }
  override ignoreEvent() {
    return true;
  }
}

export const callouts = [
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = this.build(view);
      }
      update(u: ViewUpdate) {
        if (u.docChanged || u.viewportChanged || u.selectionSet || syntaxTree(u.state) !== syntaxTree(u.startState)) {
          this.decorations = this.build(u.view);
        }
      }
      build(view: EditorView): DecorationSet {
        const deco: Range<Decoration>[] = [];
        const doc = view.state.doc;
        for (const { from, to } of view.visibleRanges) {
          syntaxTree(view.state).iterate({
            from,
            to,
            enter: (node) => {
              if (node.name !== "Blockquote") return;
              // marker sits right after the first QuoteMark
              const firstLine = doc.lineAt(node.from);
              const afterMark = firstLine.text.replace(/^\s*>\s?/, "");
              const m = MARKER_RE.exec(afterMark);
              if (!m) return;
              const type = m[1]!.toLowerCase();
              const info = CALLOUTS[type];
              if (!info) return;

              const startLine = firstLine.number;
              const endLine = doc.lineAt(node.to).number;
              for (let ln = startLine; ln <= endLine; ln++) {
                const line = doc.line(ln);
                const cls = ["hn-co", `hn-co-${type in CALLOUTS ? type : "note"}`];
                if (ln === startLine) cls.push("hn-co-first");
                if (ln === endLine) cls.push("hn-co-last");
                deco.push(
                  Decoration.line({
                    class: cls.join(" "),
                    attributes: { style: `--co-hue: ${info.hue}` },
                  }).range(line.from),
                );
              }

              // replace "[!type] " with the icon when the line isn't active
              const markerStart = firstLine.from + (firstLine.text.length - afterMark.length);
              const markerEnd = markerStart + m[0].length;
              const active =
                view.hasFocus &&
                view.state.selection.ranges.some((r) => r.from <= firstLine.to && r.to >= firstLine.from);
              if (!active) {
                deco.push(
                  Decoration.replace({ widget: new CalloutIconWidget(type) }).range(markerStart, markerEnd),
                );
              }
              return false;
            },
          });
        }
        return Decoration.set(
          deco.sort((a, b) => a.from - b.from || (a.value.startSide ?? 0) - (b.value.startSide ?? 0)),
          true,
        );
      }
    },
    { decorations: (v) => v.decorations },
  ),
  EditorView.theme({
    ".cm-line.hn-co": {
      backgroundColor: "color-mix(in srgb, var(--co-hue) 6%, transparent)",
      borderLeft: "2.5px solid var(--co-hue)",
      paddingLeft: "12px",
    },
    ".cm-line.hn-co-first": {
      borderRadius: "var(--radius-md) var(--radius-md) 0 0",
      fontWeight: "650",
    },
    ".cm-line.hn-co-last": {
      borderRadius: "0 0 var(--radius-md) var(--radius-md)",
    },
    ".hn-callout-icon": {
      display: "inline-block",
      marginRight: "8px",
      fontWeight: "700",
    },
  }),
];
